import React, { useEffect, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  X,
  Cpu,
  Server,
  HardDrive,
  Wifi,
  Activity,
  Trash2,
  Clock,
  List
} from "lucide-react";
import { serverStore } from "../../stores/server.store";
import { socketStore } from "../../stores/socket.store";
import { confirmStore } from "../../stores/confirm.store";
import toast from "react-hot-toast";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface SystemStats {
  cpu: number;
  ram: { total: number; used: number; free: number };
  disk: { total: number; used: number };
  network?: { rx_sec: number; tx_sec: number };
}

interface DetailedStats {
  cpu: {
    manufacturer: string;
    brand: string;
    cores: number;
    physicalCores: number;
    speed: number;
  };
  mem: {
    total: number;
    used: number;
    free: number;
    cached: number;
    buffers: number;
    available: number;
  };
  load: { currentLoad: number; cpus: { load: number }[] };
  disk: {
    fs: string;
    size: number;
    used: number;
    available: number;
    mount: string;
  }[];
  osInfo: {
    platform: string;
    distro: string;
    hostname: string;
    kernel: string;
  };
}

interface HistoryPoint {
  cpu: number;
  ram_used: number;
  ram_total: number;
  rx_sec: number;
  tx_sec: number;
  timestamp: number;
}

interface Process {
  pid: number;
  name: string;
  command?: string;
  cpu: number;
  mem: number;
  memRss: number;
  user: string;
}

const formatBytes = (bytes: number) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const formatSpeed = (bytes: number) => {
  if (bytes === 0) return "0 KB/s";
  const k = 1024;
  const sizes = ["B/s", "KB/s", "MB/s", "GB/s"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

// Recharts Custom Tooltip
const CustomTooltip = ({ active, payload, label, formatter }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-surface-2 border border-line p-2 rounded shadow-lg text-xs">
        <p className="text-fg-muted mb-1">{new Date(label).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {formatter ? formatter(entry.value) : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// MiniSparkline for Overview (Simplified SVG, keeping lighter weight there)
const MiniSparkline = ({
  data,
  color,
  height = 40,
}: {
  data: number[];
  color: string;
  height?: number;
}) => {
  if (data.length < 2) return <div className="h-full bg-surface-2/30 rounded flex items-center justify-center text-xs text-fg-muted">Noch keine Daten</div>;

  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;

  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = 100 - ((v - min) / range) * 100;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="w-full"
      style={{ height }}
    >
      <defs>
        <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,100 ${points} 100,100`}
        fill={`url(#gradient-${color})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

// Overview Circular Gauge
const CircularProgress = ({
  value,
  size = 80,
  strokeWidth = 8,
  color = "#ff6a3d",
}: {
  value: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (Math.min(value, 100) / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="rgba(255,255,255,0.08)"
        strokeWidth={strokeWidth}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-500 ease-in-out"
      />
    </svg>
  );
};

export const SystemResourcesPanel = observer(() => {
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'processes'>('overview');
  const [detailed, setDetailed] = useState<DetailedStats | null>(null);
  const [currentStats, setCurrentStats] = useState<SystemStats | null>(null);
  
  // History
  const [historyRange, setHistoryRange] = useState('1h');
  const [historyData, setHistoryData] = useState<HistoryPoint[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Processes
  const [processes, setProcesses] = useState<Process[]>([]);
  const [processSearch, setProcessSearch] = useState('');
  const [loadingProcesses, setLoadingProcesses] = useState(false);

  // Live History for Overview (local buffer)
  const [liveHistory, setLiveHistory] = useState<{cpu: number[], ram: number[], rx: number[], tx: number[]}>({
    cpu: [], ram: [], rx: [], tx: []
  });

  // Close on Escape (the modal otherwise only closes via the X button).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") serverStore.showSystemDetails = false; };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const socket = socketStore.rawSocket;
    if (!socket) return;

    const handleStats = (data: SystemStats) => {
      setCurrentStats(data);
      setLiveHistory(prev => ({
        cpu: [...prev.cpu, data.cpu].slice(-60),
        ram: [...prev.ram, data.ram.total ? (data.ram.used / data.ram.total) * 100 : 0].slice(-60),
        rx: [...prev.rx, data.network?.rx_sec || 0].slice(-60),
        tx: [...prev.tx, data.network?.tx_sec || 0].slice(-60)
      }));
    };

    socket.on("global_stats", handleStats);
    return () => {
      socket.off("global_stats", handleStats);
    };
  }, []);

  // Nur abfragen, solange der Monitor offen ist. Die Komponente hängt immer im Baum
  // (return null erst unten) – vorher lief die 30-s-Abfrage für jeden, auch auf der
  // Login-Seite (Live: 127x 403 „anonymous" am Tag), und ein offen gelassener
  // Prozesse-Tab fragte nach dem Schließen weiter alle 2 s ab.
  const open = serverStore.showSystemDetails;

  useEffect(() => {
    if (!open) return;
    const fetchDetailed = async () => {
      // Don't show loading on subsequent fetches
      const data = await serverStore.fetchSystemInfo();
      if (data) setDetailed(data);
    };
    fetchDetailed();
    const interval = setInterval(fetchDetailed, 30000); // 30s for static info
    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    if (open && activeTab === 'history') {
      let isMounted = true;
      const fetchHistory = async (isPolling = false) => {
        // Only show full loading state on first load ever
        if (!isPolling && historyData.length === 0) setLoadingHistory(true);
        
        const data = await serverStore.fetchSystemHistory(historyRange);
        
        if (!isMounted) return;

        const formatted = data.map((d: any) => ({
             ...d,
             ramPercent: d.ram_total ? (d.ram_used / d.ram_total) * 100 : 0,
             date: d.timestamp 
        }));
        setHistoryData(formatted);
        setLoadingHistory(false);
      };
      
      fetchHistory(false);
      const interval = setInterval(() => fetchHistory(true), 30000); // Dynamic update every 30s
      return () => { isMounted = false; clearInterval(interval); };
    }
  }, [open, activeTab, historyRange]);

  useEffect(() => {
    if (open && activeTab === 'processes') {
      let isMounted = true;
      const fetchProc = async (isPolling = false) => {
        // Only set Global loading if we have NO data and this isn't a poll
        if (!isPolling && processes.length === 0) setLoadingProcesses(true);
        
        const list = await serverStore.fetchProcesses();
        
        if (!isMounted) return;

        setProcesses(list);
        setLoadingProcesses(false);
      };
      
      // Use a ref-like check or just rely on 'isPolling' flag to avoid stale state issues causing flashes
      fetchProc(false);
      const interval = setInterval(() => fetchProc(true), 2000); // Dynamic: 2s updates
      
      return () => { isMounted = false; clearInterval(interval); };
    }
  }, [open, activeTab]); // Removed processes dependency to avoid re-setting interval constantly, solved stale closure via arg

  const handleKillProcess = async (pid: number, name: string) => {
      const ok = await confirmStore.confirm({
          title: 'Prozess beenden?',
          message: `„${name}" (PID ${pid}) wird sofort hart beendet. Ist das ein Gameserver, stürzt er ab – nicht gespeicherte Fortschritte gehen verloren.`,
          confirmText: 'Beenden',
          variant: 'danger',
          icon: 'power',
      });
      if (!ok) return;
      const success = await serverStore.killProcess(pid);
      if (success) {
          toast.success(`Prozess ${pid} beendet`);
          serverStore.fetchProcesses().then(setProcesses);
      } else {
          toast.error('Prozess konnte nicht beendet werden (fehlende Rechte?)');
      }
  };

  const ramPercent = currentStats?.ram
    ? (currentStats.ram.used / currentStats.ram.total) * 100
    : 0;

  // Bei 7/30 Tagen hilft die Uhrzeit allein nicht – dann das Datum.
  const fmtTick = (ts: number) =>
    historyRange === '1h' || historyRange === '24h'
      ? new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
      : new Date(ts).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

  if (!serverStore.showSystemDetails) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-ink border border-line rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-line bg-surface-2">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center">
              <Cpu className="mr-3 text-ember" size={24} />
              Systemmonitor
            </h2>
            <p className="text-sm text-fg-muted mt-1">
              {detailed?.osInfo?.hostname || "Server"} •{" "}
              {detailed?.osInfo?.distro || "Linux"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => (serverStore.showSystemDetails = false)}
              aria-label="Schließen"
              className="p-2 rounded-lg hover:bg-white/[0.06] text-fg-muted hover:text-fg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-line bg-surface">
            <button
                onClick={() => setActiveTab('overview')}
                className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'overview' ? 'border-ember text-fg bg-surface-2/30' : 'border-transparent text-fg-muted hover:text-fg'
                }`}
            >
                <div className="flex items-center justify-center gap-2">
                    <Activity size={16} /> Übersicht
                </div>
            </button>
            <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'history' ? 'border-ember text-fg bg-surface-2/30' : 'border-transparent text-fg-muted hover:text-fg'
                }`}
            >
                <div className="flex items-center justify-center gap-2">
                    <Clock size={16} /> Verlauf
                </div>
            </button>
            <button
                onClick={() => setActiveTab('processes')}
                className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'processes' ? 'border-ember text-fg bg-surface-2/30' : 'border-transparent text-fg-muted hover:text-fg'
                }`}
            >
                <div className="flex items-center justify-center gap-2">
                    <List size={16} /> Prozesse
                </div>
            </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-ink">
          
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
              <div className="space-y-6">
                 {/* ... (Overview UI remains similar but using shared components) ... */}
                 <div className="bg-ink/40 rounded-xl p-5 border border-line/50">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-sm font-bold text-white flex items-center gap-2">
                           <Cpu size={18} className="text-ember" />
                           CPU-Auslastung
                        </h3>
                        {detailed?.cpu && <p className="text-xs text-fg-muted mt-1">{detailed.cpu.brand} • {detailed.cpu.physicalCores} Kerne</p>}
                      </div>
                      <span className="text-2xl font-bold text-white">{Math.round(currentStats?.cpu || 0)}%</span>
                    </div>
                    <div className="h-24 mb-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={liveHistory.cpu.map((v, i) => ({ i, value: v }))}>
                                <defs>
                                    <linearGradient id="colorCpuLive" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#ff6a3d" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#ff6a3d" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <Tooltip 
                                    content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                        return (
                                            <div className="bg-surface-2 border border-line p-1.5 rounded shadow-lg text-xs">
                                                <p className="text-ember font-bold">{Number(payload[0].value).toFixed(1)}%</p>
                                            </div>
                                        );
                                        }
                                        return null;
                                    }}
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="value" 
                                    stroke="#ff6a3d" 
                                    strokeWidth={2}
                                    fill="url(#colorCpuLive)" 
                                    isAnimationActive={false}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                    {/* Cores */}
                    {detailed?.load?.cpus && (
                      <div className="grid grid-cols-8 md:grid-cols-12 gap-1.5 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300">
                        {detailed.load.cpus.map((core, i) => (
                          <div key={i} className="bg-surface-2/40 rounded p-1 text-center" title={`Kern ${i + 1}: ${Math.round(core.load)}%`}>
                             <div className="h-6 w-full bg-surface-2 rounded-sm relative overflow-hidden flex items-end">
                                <div 
                                    className={`w-full transition-all duration-500 ${core.load > 85 ? 'bg-red-500' : core.load > 60 ? 'bg-yellow-500' : 'bg-ember/70'}`}
                                    style={{ height: `${core.load}%` }}
                                ></div>
                             </div>
                          </div>
                        ))}
                      </div>
                    )}
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Memory */}
                    <div className="bg-ink/40 rounded-xl p-5 border border-line/50">
                         <h3 className="text-sm font-bold text-white flex items-center mb-5 gap-2">
                            <Server size={18} className="text-online" /> Arbeitsspeicher (RAM)
                         </h3>
                         <div className="flex items-center gap-6">
                            <div className="relative">
                                <CircularProgress value={ramPercent} color="#37d9a0" size={100} strokeWidth={10} />
                                <div className="absolute inset-0 flex items-center justify-center flex-col">
                                    <span className="text-xl font-bold text-white">{Math.round(ramPercent)}%</span>
                                </div>
                            </div>
                            <div className="flex-1 space-y-3">
                                <div className="flex justify-between text-sm border-b border-line/50 pb-2">
                                     <span className="text-fg-muted">Belegt</span>
                                     <span className="text-white font-mono">{formatBytes(currentStats?.ram?.used || 0)}</span>
                                </div>
                                <div className="flex justify-between text-sm border-b border-line/50 pb-2">
                                     <span className="text-fg-muted">Frei</span>
                                     <span className="text-white font-mono">{formatBytes(currentStats?.ram?.free || 0)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                     <span className="text-fg-muted">Gesamt</span>
                                     <span className="text-fg-muted font-mono">{formatBytes(currentStats?.ram?.total || 0)}</span>
                                </div>
                            </div>
                         </div>
                    </div>

                    {/* Network */}
                    <div className="bg-ink/40 rounded-xl p-5 border border-line/50">
                        <h3 className="text-sm font-bold text-white flex items-center mb-5 gap-2">
                            <Wifi size={18} className="text-ember" /> Netzwerk
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-surface-2/30 rounded-lg p-3">
                                <div className="text-xs text-fg-muted mb-1 flex items-center gap-1">Download</div>
                                <div className="text-xl font-bold text-green-400">{formatSpeed(currentStats?.network?.rx_sec || 0)}</div>
                                <div className="h-10 mt-2">
                                    <MiniSparkline data={liveHistory.rx} color="#37d9a0" height={40} />
                                </div>
                            </div>
                            <div className="bg-surface-2/30 rounded-lg p-3">
                                <div className="text-xs text-fg-muted mb-1 flex items-center gap-1">Upload</div>
                                <div className="text-xl font-bold text-ember">{formatSpeed(currentStats?.network?.tx_sec || 0)}</div>
                                <div className="h-10 mt-2">
                                    <MiniSparkline data={liveHistory.tx} color="#ff6a3d" height={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                 </div>

                 {/* Storage */}
                 <div className="bg-ink/40 rounded-xl p-5 border border-line/50">
                    <h3 className="text-sm font-bold text-white flex items-center mb-5 gap-2">
                        <HardDrive size={18} className="text-ember" /> Laufwerke
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {detailed?.disk?.map((d, i) => {
                             const usedPercent = (d.used / d.size) * 100;
                             return (
                                 <div key={i} className="bg-surface-2/30 rounded-lg p-3 border border-line">
                                     <div className="flex justify-between items-start mb-2">
                                         <div>
                                            <div className="text-sm font-bold text-fg">{d.mount}</div>
                                            <div className="text-[10px] text-fg-muted">{d.fs}</div>
                                         </div>
                                         <div className={`text-xs font-bold px-1.5 py-0.5 rounded ${usedPercent > 90 ? 'bg-red-500/20 text-red-400' : 'bg-gray-700 text-fg-muted'}`}>
                                            {Math.round(usedPercent)}%
                                         </div>
                                     </div>
                                     <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                                         <div className={`h-full rounded-full ${usedPercent > 90 ? 'bg-red-500' : 'bg-ember'}`} style={{ width: `${usedPercent}%` }}></div>
                                     </div>
                                     <div className="flex justify-between text-xs text-fg-muted">
                                         <span>{formatBytes(d.used)} belegt</span>
                                         <span>{formatBytes(d.size)} gesamt</span>
                                     </div>
                                 </div>
                             );
                        })}
                    </div>
                 </div>
              </div>
          )}

          {/* HISTORY TAB - NOW WITH RECHARTS */}
          {activeTab === 'history' && (
              <div className="space-y-6">
                 <div className="flex justify-end">
                    <div className="flex bg-ink rounded-lg p-1 border border-line">
                        {[{l:'1 Std.', v:'1h'}, {l:'24 Std.', v:'24h'}, {l:'7 Tage', v:'7d'}, {l:'30 Tage', v:'30d'}].map((tr) => (
                           <button
                             key={tr.v}
                             onClick={() => setHistoryRange(tr.v)}
                             className={`px-3 py-1 text-xs font-medium rounded transition-all ${
                               historyRange === tr.v ? "bg-ember text-ink" : "text-fg-muted hover:text-white"
                             }`}
                           >
                             {tr.l}
                           </button>
                        ))}
                    </div>
                 </div>

                 {loadingHistory ? (
                     <div className="text-center py-20 text-fg-muted animate-pulse">Lade Verlauf …</div>
                 ) : historyData.length === 0 ? (
                     <div className="text-center py-20 text-fg-muted">Noch keine Verlaufsdaten vorhanden.</div>
                 ) : (
                     <>
                        <div className="bg-ink/40 rounded-xl p-5 border border-line/50">
                            <div className="mb-4 text-sm font-bold text-fg flex items-center gap-2"><Cpu size={16}/> CPU-Auslastung</div>
                            <div className="h-64 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={historyData}>
                                        <defs>
                                            <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#ff6a3d" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#ff6a3d" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                                        <XAxis 
                                            dataKey="date" 
                                            tick={{fontSize: 10, fill: '#9ca3af'}} 
                                            tickFormatter={fmtTick}
                                            interval="preserveStartEnd"
                                        />
                                        <YAxis tick={{fontSize: 10, fill: '#9ca3af'}} unit="%" domain={[0, 100]} />
                                        <Tooltip content={<CustomTooltip formatter={(v: number) => `${v.toFixed(1)}%`} />} />
                                        <Area type="monotone" dataKey="cpu" stroke="#ff6a3d" fillOpacity={1} fill="url(#colorCpu)" name="CPU" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="bg-ink/40 rounded-xl p-5 border border-line/50">
                            <div className="mb-4 text-sm font-bold text-fg flex items-center gap-2"><Server size={16}/> Arbeitsspeicher</div>
                            <div className="h-64 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={historyData}>
                                        <defs>
                                            <linearGradient id="colorRam" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#37d9a0" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#37d9a0" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                                        <XAxis 
                                            dataKey="date" 
                                            tick={{fontSize: 10, fill: '#9ca3af'}} 
                                            tickFormatter={fmtTick}
                                        />
                                        <YAxis tick={{fontSize: 10, fill: '#9ca3af'}} unit="%" domain={[0, 100]} />
                                        <Tooltip content={<CustomTooltip formatter={(v: number) => `${v.toFixed(1)}%`} />} />
                                        <Area type="monotone" dataKey="ramPercent" stroke="#37d9a0" fillOpacity={1} fill="url(#colorRam)" name="RAM" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="bg-ink/40 rounded-xl p-5 border border-line/50">
                            <div className="mb-4 text-sm font-bold text-fg flex items-center gap-2"><Wifi size={16}/> Netzwerk (Download & Upload)</div>
                            <div className="h-64 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={historyData}>
                                        <defs>
                                            <linearGradient id="colorRx" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#37d9a0" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#37d9a0" stopOpacity={0}/>
                                            </linearGradient>
                                            <linearGradient id="colorTx" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#ff9d42" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#ff9d42" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                                        <XAxis 
                                            dataKey="date" 
                                            tick={{fontSize: 10, fill: '#9ca3af'}} 
                                            tickFormatter={fmtTick}
                                        />
                                        <YAxis tick={{fontSize: 10, fill: '#9ca3af'}} tickFormatter={formatSpeed} />
                                        <Tooltip content={<CustomTooltip formatter={(v: number) => formatSpeed(v)} />} />
                                        <Area type="monotone" dataKey="rx_sec" stroke="#37d9a0" fillOpacity={1} fill="url(#colorRx)" name="Download" />
                                        <Area type="monotone" dataKey="tx_sec" stroke="#ff9d42" fillOpacity={1} fill="url(#colorTx)" name="Upload" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                     </>
                 )}
              </div>
          )}

          {/* PROCESSES TAB */}
          {activeTab === 'processes' && (
              <div className="space-y-4">
                  <div className="flex justify-between items-center bg-ink/50 p-3 rounded-lg border border-line">
                      <div className="text-sm text-fg-muted">
                          Prozesse gesamt: <span className="text-white font-bold ml-1">{processes.length}</span>
                      </div>
                      <div className="flex text-xs text-fg-muted gap-4">
                          <span>aktualisiert alle 2 s · die 50 mit der meisten CPU</span>
                      </div>
                      <input 
                         type="text" 
                         placeholder="Prozess oder Befehl suchen …" 
                         value={processSearch}
                         onChange={(e) => setProcessSearch(e.target.value)}
                         className="bg-gray-950 border border-line rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-ember/60 w-72 transition-all"
                      />
                  </div>

                  <div className="bg-ink/40 rounded-xl border border-line/50 overflow-hidden shadow-sm">
                      <div className="overflow-x-auto max-h-[600px]">
                          <table className="w-full text-left text-sm relative">
                              <thead className="bg-surface-2 text-fg sticky top-0 z-10 shadow-lg">
                                  <tr>
                                      <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider">PID</th>
                                      <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider w-1/3">Name / Befehl</th>
                                      <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider">Benutzer</th>
                                      <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-right">CPU</th>
                                      <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-right">RAM</th>
                                      <th className="px-4 py-3 text-right"><span className="sr-only">Aktion</span></th>
                                  </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-800/30">
                                  {loadingProcesses ? (
                                      <tr><td colSpan={6} className="px-4 py-8 text-center text-fg-muted italic">Lade Prozesse …</td></tr>
                                  ) : Array.isArray(processes) ? processes
                                        .slice() 
                                        .sort((a, b) => (b.cpu || 0) - (a.cpu || 0)) 
                                        .filter(p => {
                                             if(!processSearch) return true;
                                             const term = processSearch.toLowerCase();
                                             return (
                                                 (p?.name || "").toLowerCase().includes(term) || 
                                                 (p?.command || "").toLowerCase().includes(term) ||
                                                 (p?.pid || "").toString().includes(term)
                                             );
                                        })
                                        .slice(0, 50) 
                                        .map((p) => {
                                           if (!p) return null;
                                           // Calculate Mem % if p.mem is 0 but we have RSS and Total
                                           const memUsage = p.mem > 0 ? p.mem : (p.memRss && currentStats?.ram?.total) ? (p.memRss / currentStats.ram.total) * 100 : 0;
                                           
                                           return (
                                      <tr key={p.pid} className="hover:bg-white/[0.03] transition-colors group">
                                          <td className="px-4 py-2 font-mono text-fg-muted group-hover:text-fg">{p.pid}</td>
                                          <td className="px-4 py-2 text-white font-medium">
                                              <div className="text-fg">{p.name}</div>
                                              {p.command && (
                                                  <div className="text-[10px] text-fg-muted truncate max-w-[300px] font-mono opacity-70 group-hover:opacity-100">{p.command}</div>
                                              )}
                                          </td>
                                          <td className="px-4 py-2 text-fg-muted">{p.user}</td>
                                          <td className="px-4 py-2 text-right font-mono text-ember font-bold">{(p.cpu || 0).toFixed(1)}%</td>
                                          <td className="px-4 py-2 text-right font-mono text-green-400 font-bold">{memUsage.toFixed(1)}%</td>
                                          <td className="px-4 py-2 text-right">
                                              <button 
                                                  onClick={() => handleKillProcess(p.pid, p.name)}
                                                  className="p-1.5 rounded bg-red-500/10 text-red-500 hover:bg-red-600 hover:text-white transition-all opacity-40 group-hover:opacity-100 focus:opacity-100"
                                                  title="Prozess beenden"
                                                  aria-label="Prozess beenden"
                                              >
                                                  <Trash2 size={14} />
                                              </button>
                                          </td>
                                      </tr>
                                      );
                                        }) : null}
                              </tbody>
                          </table>
                      </div>
                  </div>
              </div>
          )}

        </div>
      </div>
    </div>
  );
});
